import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { setAuthContext } from '../utils/fetchWithAuth';

const AuthContext = createContext();
const API_TARGET = import.meta.env.VITE_API_TARGET || 'http://localhost:5174';

const normalizeStringList = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(Boolean);
};

const normalizePermissionCodes = (codes) => {
  if (Array.isArray(codes)) {
    return normalizeStringList(codes);
  }

  return codes ? [codes] : [];
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const isHandlingUnauthorizedRef = useRef(false);

  const resetAuthState = useCallback(() => {
    setUser(null);
    setIsAdmin(false);
    setIsActive(false);
    setRoles([]);
    setPermissions(new Set());
    setIsAuthenticated(false);
  }, []);

  const applyAuthData = useCallback((userData) => {
    setUser(userData.user_info ?? null);
    setIsAdmin(userData.is_admin === true);
    setIsActive(userData.is_active !== false);
    setRoles(normalizeStringList(userData.roles));
    setPermissions(new Set(normalizeStringList(userData.permissions)));
    setIsAuthenticated(true);
  }, []);

  const handleUnauthorized = useCallback(() => {
    if (isHandlingUnauthorizedRef.current) {
      return;
    }

    isHandlingUnauthorizedRef.current = true;

    resetAuthState();
    setLoading(false);

    const pathname = window.location.pathname;
    const isLoginPage = pathname === '/' ||
                        pathname === '/login' ||
                        pathname.startsWith('/auth/callback') ||
                        pathname.startsWith('/auth/google/callback');

    if (!isLoginPage) {
      window.location.replace('/login');
      return;
    }

    isHandlingUnauthorizedRef.current = false;
  }, [resetAuthState]);

  const handleForbidden = useCallback((message = 'Недостаточно прав') => {
    window.dispatchEvent(
      new window.CustomEvent('creative-platform:forbidden', {
        detail: { message },
      }),
    );
  }, []);

  const checkAuthStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_TARGET}/api/auth/me`, {
        credentials: 'include', 
      });
      
      if (response.status === 401) {
        resetAuthState();
        setLoading(false);
        return;
      }
      
      if (response.ok) {
        const userData = await response.json();
        applyAuthData(userData);
      } else {
        resetAuthState();
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      resetAuthState();
    } finally {
      setLoading(false);
    }
  }, [applyAuthData, resetAuthState]);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  useEffect(() => {
    setAuthContext({
      handleUnauthorized,
      handleForbidden,
    });
  }, [handleForbidden, handleUnauthorized]);

  const login = useCallback(async () => {
    try {
      window.location.href = `${API_TARGET}/api/auth/login`;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    const response = await fetch(`${API_TARGET}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Could not log out');
    }
    resetAuthState();
  }, [resetAuthState]);

  const hasPermission = useCallback((codename) => {
    if (!codename || isAdmin) {
      return true;
    }

    return permissions.has(codename);
  }, [isAdmin, permissions]);

  const hasAnyPermission = useCallback((codes) => {
    const normalizedCodes = normalizePermissionCodes(codes);

    if (normalizedCodes.length === 0 || isAdmin) {
      return true;
    }

    return normalizedCodes.some((code) => permissions.has(code));
  }, [isAdmin, permissions]);

  const hasAllPermissions = useCallback((codes) => {
    const normalizedCodes = normalizePermissionCodes(codes);

    if (normalizedCodes.length === 0 || isAdmin) {
      return true;
    }

    return normalizedCodes.every((code) => permissions.has(code));
  }, [isAdmin, permissions]);

  const permissionList = useMemo(
    () => Array.from(permissions),
    [permissions],
  );

  const value = useMemo(() => ({
    user,
    isAdmin,
    isActive,
    roles,
    permissions,
    permissionList,
    loading,
    isAuthenticated,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    login,
    logout,
    checkAuthStatus,
  }), [
    checkAuthStatus,
    hasAllPermissions,
    hasAnyPermission,
    hasPermission,
    isActive,
    isAdmin,
    isAuthenticated,
    loading,
    login,
    logout,
    permissionList,
    permissions,
    roles,
    user,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
